import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { AdminPageHeader, AdminPageShell, AdminTabs, NoticePanel, StatCard } from './AdminPageUI';

const emptyForm = { name: '', email: '', phone: '', password: 'parent123', children: [] };
const safeText = (...parts) => parts.filter(Boolean).join(' ').toLowerCase();

const ParentManagement = () => {
  const navigate = useNavigate();
  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [activeTab, setActiveTab] = useState('parents');
  const [search, setSearch] = useState('');
  const [childSearch, setChildSearch] = useState('');
  const [linkSelections, setLinkSelections] = useState({});
  const [expandedParents, setExpandedParents] = useState(new Set());
  const [editForms, setEditForms] = useState({});
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const [parentsRes, studentsRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.BASE_URL}/api/parents`),
        fetch(API_ENDPOINTS.STUDENTS)
      ]);
      setParents(await parentsRes.json());
      setStudents(await studentsRes.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const assignedUsernames = useMemo(() => new Set(
    parents.flatMap((parent) => parent.childUsernames || parent.children?.map((child) => child.username) || [])
  ), [parents]);

  const unassignedStudents = useMemo(
    () => students.filter((student) => !assignedUsernames.has(student.username)),
    [students, assignedUsernames]
  );

  const filteredStudents = useMemo(() => {
    const term = childSearch.trim().toLowerCase();
    if (!term) return students;
    return students.filter((student) =>
      safeText(
        student.firstName,
        student.lastName,
        `${student.firstName || ''}${student.lastName || ''}`,
        student.username,
        student.className,
        student.parentContact
      ).includes(term)
    );
  }, [students, childSearch]);

  const filteredParents = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return parents;
    return parents.filter((parent) =>
      `${parent.name} ${parent.email} ${parent.phone} ${parent.children?.map((child) => child.username).join(' ')}`.toLowerCase().includes(term)
    );
  }, [parents, search]);

  const createParent = async (event) => {
    event.preventDefault();
    const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/parents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form)
    });
    const data = await response.json();
    if (!response.ok) {
      alert(data.error || 'Failed to create parent');
      return;
    }
    setForm(emptyForm);
    setChildSearch('');
    loadData();
  };

  const toggleChild = (username) => {
    const children = new Set(form.children);
    if (children.has(username)) children.delete(username);
    else children.add(username);
    setForm({ ...form, children: Array.from(children) });
  };

  const linkChild = async (parentId) => {
    const username = linkSelections[parentId];
    if (!username) return;
    await fetch(`${API_ENDPOINTS.BASE_URL}/api/parents/${parentId}/link-child`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username })
    });
    setLinkSelections({ ...linkSelections, [parentId]: '' });
    loadData();
  };

  const unlinkChild = async (parentId, username) => {
    await fetch(`${API_ENDPOINTS.BASE_URL}/api/parents/${parentId}/link-child/${username}`, {
      method: 'DELETE'
    });
    loadData();
  };

  const toggleParentOpen = (parent) => {
    const next = new Set(expandedParents);
    if (next.has(parent.id)) next.delete(parent.id);
    else {
      next.add(parent.id);
      setEditForms((current) => ({
        ...current,
        [parent.id]: current[parent.id] || {
          name: parent.name || '',
          email: parent.email || '',
          phone: parent.phone || '',
          videoCallStart: parent.videoCallStart || '',
          videoCallEnd: parent.videoCallEnd || ''
        }
      }));
    }
    setExpandedParents(next);
  };

  const updateParent = async (parentId) => {
    const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/parents/${parentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editForms[parentId])
    });
    const data = await response.json();
    if (!response.ok) {
      alert(data.error || 'Failed to update parent');
      return;
    }
    loadData();
  };

  const resetParentPassword = async (parent) => {
    if (!window.confirm(`Reset password for ${parent.name}? Default password will be parent123 and the parent must change it on next login.`)) return;
    const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/parents/${parent.id}/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: 'parent123' })
    });
    const data = await response.json();
    if (!response.ok) {
      alert(data.error || 'Failed to reset parent password');
      return;
    }
    alert(`Password reset. Default password: ${data.defaultPassword}`);
    loadData();
  };

  const tabs = [
    { id: 'parents', label: 'Parent Accounts', count: parents.length },
    { id: 'create', label: 'Create & Link' },
    { id: 'unassigned', label: 'Unassigned Students', count: unassignedStudents.length }
  ];

  return (
    <AdminPageShell>
      <AdminPageHeader
        eyebrow="People"
        title="Parents Management"
        description="Create guardian accounts, link students explicitly, and keep fee/payment ownership visible from the parent record."
        actions={<button onClick={() => navigate('/dashboard')} className="bg-slate-900 text-white px-5 py-3 rounded-lg font-bold">Dashboard</button>}
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Parents" value={parents.length} tone="sky" detail="Active guardian accounts" />
        <StatCard label="Linked Students" value={assignedUsernames.size} tone="emerald" detail="Assigned or matched by contact" />
        <StatCard label="Need Parent Link" value={unassignedStudents.length} tone={unassignedStudents.length ? 'rose' : 'emerald'} detail="Students without a guardian account" />
        <StatCard label="Students" value={students.length} tone="violet" detail="Available to assign" />
      </div>

      <NoticePanel title="Parent-student assignment rule" tone="sky">
        Students can be linked directly to a parent account. If a student's parent contact email matches the parent email, the system also shows that child as a contact match so older records are not lost.
      </NoticePanel>

      <AdminTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'create' && (
        <form onSubmit={createParent} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <input className="border rounded-lg px-3 py-3" placeholder="Parent name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <input className="border rounded-lg px-3 py-3" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          <input className="border rounded-lg px-3 py-3" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <input className="border rounded-lg px-3 py-3" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          <div className="md:col-span-2 lg:col-span-4 space-y-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <p className="font-black text-slate-900">Link children now</p>
              <input className="border rounded-lg px-3 py-2 md:w-80" placeholder="Search student, username, or class" value={childSearch} onChange={(e) => setChildSearch(e.target.value)} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 max-h-80 overflow-y-auto pr-1">
              {filteredStudents.map((student) => {
                const selected = form.children.includes(student.username);
                return (
                  <button
                    type="button"
                    key={student.username}
                    onClick={() => toggleChild(student.username)}
                    className={`text-left rounded-lg border p-3 shadow-sm transition ${
                      selected
                        ? 'bg-emerald-100 border-emerald-400 text-emerald-950'
                        : 'bg-slate-50 border-slate-300 text-slate-950'
                    }`}
                  >
                    <p className="font-black text-slate-950">{student.firstName} {student.lastName}</p>
                    <p className="text-sm text-slate-700">{student.username} {student.className ? `- ${student.className}` : ''}</p>
                  </button>
                );
              })}
              {!filteredStudents.length && (
                <div className="md:col-span-2 xl:col-span-3 border border-amber-200 bg-amber-50 text-amber-900 rounded-lg p-4">
                  No students matched "{childSearch}". Try the child's name, username, class, or parent contact.
                </div>
              )}
            </div>
          </div>
          <button className="bg-sky-700 text-white px-5 py-3 rounded-lg font-bold md:col-span-2 lg:col-span-4">Create Parent Account</button>
        </form>
      )}

      {activeTab === 'parents' && (
        <div className="space-y-4">
          <input className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm" placeholder="Search parents, children, email, or phone" value={search} onChange={(e) => setSearch(e.target.value)} />
          {loading ? <div className="bg-white rounded-xl p-6">Loading parents...</div> : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              {filteredParents.map((parent) => (
                <div key={parent.id} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-4">
                  <div className="flex justify-between gap-4">
                    <div>
                      <h2 className="text-xl font-black text-slate-950">{parent.name}</h2>
                      <p className="text-slate-600">{parent.email}</p>
                      <p className="text-slate-500">{parent.phone || 'No phone saved'}</p>
                      {parent.forcePasswordChange && <p className="text-sm font-bold text-amber-700 mt-1">Password reset pending</p>}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full h-fit text-sm font-bold">{parent.children?.length || 0} linked</span>
                      <button type="button" onClick={() => toggleParentOpen(parent)} className="border border-slate-300 text-slate-800 px-3 py-2 rounded-lg text-sm font-bold">
                        {expandedParents.has(parent.id) ? 'Close' : 'View / Edit'}
                      </button>
                    </div>
                  </div>
                  {expandedParents.has(parent.id) && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
                        <input className="border rounded-lg px-3 py-2" value={editForms[parent.id]?.name || ''} onChange={(e) => setEditForms({ ...editForms, [parent.id]: { ...editForms[parent.id], name: e.target.value } })} placeholder="Name" />
                        <input className="border rounded-lg px-3 py-2" value={editForms[parent.id]?.email || ''} onChange={(e) => setEditForms({ ...editForms, [parent.id]: { ...editForms[parent.id], email: e.target.value } })} placeholder="Email" />
                        <input className="border rounded-lg px-3 py-2" value={editForms[parent.id]?.phone || ''} onChange={(e) => setEditForms({ ...editForms, [parent.id]: { ...editForms[parent.id], phone: e.target.value } })} placeholder="Phone" />
                        <input type="time" className="border rounded-lg px-3 py-2" value={editForms[parent.id]?.videoCallStart || ''} onChange={(e) => setEditForms({ ...editForms, [parent.id]: { ...editForms[parent.id], videoCallStart: e.target.value } })} title="Video call start time" />
                        <input type="time" className="border rounded-lg px-3 py-2" value={editForms[parent.id]?.videoCallEnd || ''} onChange={(e) => setEditForms({ ...editForms, [parent.id]: { ...editForms[parent.id], videoCallEnd: e.target.value } })} title="Video call end time" />
                        <button type="button" onClick={() => updateParent(parent.id)} className="bg-sky-700 text-white px-4 py-2 rounded-lg font-bold">Save Info</button>
                        <button type="button" onClick={() => resetParentPassword(parent)} className="bg-amber-600 text-white px-4 py-2 rounded-lg font-bold">Reset Password</button>
                      </div>
                      <div className="space-y-2">
                        {(parent.children || []).map((child) => (
                          <div key={child.username} className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 bg-slate-50 rounded-lg px-3 py-2">
                            <div>
                              <p className="font-bold text-slate-900">{child.firstName} {child.lastName}</p>
                              <p className="text-sm text-slate-500">{child.username} {child.className ? `- ${child.className}` : ''} - {child.assignmentSource === 'parentContact' ? 'contact match' : 'assigned'}</p>
                            </div>
                            <button onClick={() => unlinkChild(parent.id, child.username)} className="border border-rose-200 text-rose-700 px-3 py-2 rounded-lg text-sm font-bold disabled:opacity-50" disabled={child.assignmentSource === 'parentContact'} title={child.assignmentSource === 'parentContact' ? 'Update the student parent contact to remove this match.' : 'Unlink child'}>
                              Unlink
                            </button>
                          </div>
                        ))}
                        {!parent.children?.length && <p className="text-sm text-slate-500 bg-slate-50 rounded-lg p-3">No children linked yet.</p>}
                      </div>
                      <div className="flex flex-col md:flex-row gap-2">
                        <select value={linkSelections[parent.id] || ''} onChange={(e) => setLinkSelections({ ...linkSelections, [parent.id]: e.target.value })} className="flex-1 border rounded-lg px-3 py-3">
                          <option value="">Select child to link</option>
                          {students.map((student) => <option key={student.username} value={student.username}>{student.firstName} {student.lastName} ({student.username})</option>)}
                        </select>
                        <button type="button" onClick={() => linkChild(parent.id)} className="bg-slate-900 text-white px-4 py-3 rounded-lg font-bold">Link</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'unassigned' && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
          <h2 className="text-xl font-black text-slate-950 mb-4">Students needing a parent account</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {unassignedStudents.map((student) => (
              <div key={student.username} className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                <p className="font-bold text-amber-950">{student.firstName} {student.lastName}</p>
                <p className="text-sm text-amber-800">{student.username} {student.className ? `- ${student.className}` : ''}</p>
                <p className="text-xs text-amber-700 mt-1">{student.parentContact || 'No parent contact on student record'}</p>
              </div>
            ))}
            {!unassignedStudents.length && <p className="text-slate-600">Every student is currently linked or matched to a parent.</p>}
          </div>
        </div>
      )}
    </AdminPageShell>
  );
};

export default ParentManagement;
